import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid
} from 'recharts';
import { ChevronDown, Calendar, Sparkles } from 'lucide-react';
import { companyApi } from '../../api/companyApi';

const RevenueChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('Yearly');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const res = await companyApi.getCompanies({ limit: 100 });
        const companies = res?.data?.companies || res?.companies || [];

        // Group companies by month
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const currentYear = new Date().getFullYear();
        const monthlyStats = Array.from({ length: 12 }, (_, i) => ({
          monthNum: i,
          month: months[i],
          companies: 0,
          employees: 0
        }));

        companies.forEach(c => {
          const date = new Date(c.createdAt || c.onboardedAt || Date.now());
          if (date.getFullYear() === currentYear) {
            const m = date.getMonth();
            monthlyStats[m].companies += 1;
            monthlyStats[m].employees += (c.currentEmployeeCount || 0);
          }
        });

        // Compute cumulative growth
        let cumulativeCompanies = 0;
        let cumulativeEmployees = 0;
        const cumulativeData = monthlyStats.map(stat => {
          cumulativeCompanies += stat.companies;
          cumulativeEmployees += stat.employees;
          return {
            month: stat.month,
            companies: cumulativeCompanies,
            employees: cumulativeEmployees
          };
        });

        // Filter to only show months up to the current month
        const currentMonth = new Date().getMonth();
        setChartData(cumulativeData.slice(0, currentMonth + 1));
      } catch (err) {
        console.error('Failed to load chart data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, []);

  return (
    <div className="bg-white/90 backdrop-blur-xl rounded-2xl border border-slate-200/80 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.03)] p-6 h-full flex flex-col justify-between">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-extrabold text-slate-900 font-display tracking-tight">Tenant & Employee Scale</h3>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200/60 text-[10px] font-extrabold text-emerald-700 uppercase">
              <Sparkles className="w-2.5 h-2.5" />
              Live Dynamics
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">Cumulative monthly trajectory of onboarded multi-tenant organizations</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex items-center gap-4 text-xs font-semibold text-slate-600 bg-slate-50/80 px-3.5 py-1.5 rounded-xl border border-slate-200/60">
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-md bg-gradient-to-r from-cyan-500 to-blue-600 shadow-sm" />
              Employees
            </span>
            <span className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-md bg-gradient-to-r from-emerald-500 to-teal-600 shadow-sm" />
              Companies
            </span>
          </div>
          
          <div className="relative">
            <button 
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-2 px-3.5 py-1.5 rounded-xl border border-slate-200 text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 transition shadow-sm"
            >
              <Calendar className="w-3.5 h-3.5 text-slate-400" />
              <span>{timeRange}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-1 w-32 rounded-xl bg-white border border-slate-200 shadow-lg p-1 z-30 text-xs">
                {['Yearly', 'Quarterly', 'Monthly'].map(option => (
                  <button
                    key={option}
                    onClick={() => {
                      setTimeRange(option);
                      setDropdownOpen(false);
                    }}
                    className={`w-full text-left px-3 py-1.5 rounded-lg font-medium transition ${timeRange === option ? 'bg-emerald-50 text-emerald-700 font-bold' : 'hover:bg-slate-50 text-slate-600'}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chart Canvas */}
      <div className="flex-1 w-full relative min-h-0">
        <div className="absolute inset-0 flex items-center justify-center">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-slate-400 font-medium">
              <span className="w-4 h-4 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin" />
              Loading chart metrics...
            </div>
          ) : chartData.length === 0 || chartData.every(d => d.companies === 0) ? (
            <div className="text-xs text-slate-400 font-medium">No tenant scale data available for this year yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barGap={8}>
                <defs>
                  <linearGradient id="companiesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                    <stop offset="100%" stopColor="#0d9488" stopOpacity={0.85} />
                  </linearGradient>
                  <linearGradient id="employeesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={1} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.85} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis 
                  dataKey="month" 
                  stroke="#94a3b8" 
                  tickLine={false} 
                  axisLine={false} 
                  fontSize={11} 
                  dy={10} 
                  tick={{ fill: '#64748b', fontWeight: 600 }}
                />
                <YAxis 
                  stroke="#94a3b8" 
                  tickLine={false} 
                  axisLine={false} 
                  fontSize={11} 
                  dx={-10}
                  tick={{ fill: '#64748b', fontWeight: 600 }}
                />
                <Tooltip
                  cursor={{ fill: 'rgba(241, 245, 249, 0.5)' }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-slate-900/95 text-white p-3.5 rounded-2xl shadow-xl border border-slate-800 backdrop-blur-md min-w-[140px]">
                          <p className="text-xs font-bold font-display text-slate-300 border-b border-slate-800 pb-1.5 mb-2">
                            {label} Metrics
                          </p>
                          <div className="space-y-1.5 text-xs">
                            <div className="flex items-center justify-between text-cyan-400">
                              <span className="font-medium">Employees:</span>
                              <span className="font-extrabold">{payload[0]?.value}</span>
                            </div>
                            <div className="flex items-center justify-between text-emerald-400">
                              <span className="font-medium">Companies:</span>
                              <span className="font-extrabold">{payload[1]?.value}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="employees" 
                  fill="url(#employeesGradient)" 
                  radius={[6, 6, 0, 0]} 
                  barSize={14} 
                />
                <Bar 
                  dataKey="companies" 
                  fill="url(#companiesGradient)" 
                  radius={[6, 6, 0, 0]} 
                  barSize={14} 
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueChart;

